import { useMemo, useState } from "react";
import { useGetIngredientsQuery } from "@/redux/services/inventory/ingredient.api";
import {
  Ingredient,
  RecipeIngredientLink,
} from "@/redux/services/inventory/inventory.type";
import {
  Button,
  ButtonAdd,
  ButtonDelete,
  ButtonEdit,
} from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToggle } from "@/lib/hooks";
import Modal from "@/components/Modal";
import AsyncCombobox from "@/components/inventory/AsyncCombobox";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { formatStock, normalizeDecimalInput } from "@/lib/utils";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";

export interface RecipeIngredientsManagerProps {
  restaurantId: string;
  linkedIngredients: RecipeIngredientLink[];
  isLoading: boolean;
  onLink: (args: {
    ingredient: string;
    quantity: string;
  }) => Promise<{ ok: boolean; message?: string }>;
  onRemove: (
    mapping: RecipeIngredientLink
  ) => Promise<{ ok: boolean; message?: string }>;
  onUpdate?: (
    mapping: RecipeIngredientLink,
    args: { quantity: string }
  ) => Promise<{ ok: boolean; message?: string }>;
  isLinking?: boolean;
  isUpdating?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  addLabel?: string;
  /** List only, no link/edit/remove (users with inventory.view but not
   * inventory.manage; the API refuses their writes). */
  readOnly?: boolean;
}

// Linked quantities come back in the canonical DECIMAL(14,6) form
// (e.g. "0.750000"); trim the trailing zeros for a friendlier edit field.
const trimQuantity = (quantity: string): string =>
  quantity.includes(".")
    ? quantity.replace(/0+$/, "").replace(/\.$/, "")
    : quantity;

const RecipeIngredientsManager = ({
  restaurantId,
  linkedIngredients,
  isLoading,
  onLink,
  onRemove,
  onUpdate,
  isLinking = false,
  isUpdating = false,
  emptyTitle = "Aucun ingrédient lié",
  emptySubtitle = "Liez des ingrédients pour suivre le stock automatiquement",
  addLabel = "Lier un ingrédient",
  readOnly = false,
}: RecipeIngredientsManagerProps) => {
  const { data: ingredientsData, isFetching: isFetchingIngredients } =
    useGetIngredientsQuery({
      restaurantId,
      query: { pagination: false },
    });

  const { isOpen, open, close } = useToggle();
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingMapping, setEditingMapping] =
    useState<RecipeIngredientLink | null>(null);
  const [editQuantity, setEditQuantity] = useState("");
  const confirm = useConfirm();

  const allIngredients: Ingredient[] = ingredientsData?.results ?? [];

  const linkedIds = useMemo(
    () => new Set(linkedIngredients.map((li) => li.ingredient.id)),
    [linkedIngredients]
  );
  const availableIngredients = useMemo(
    () => allIngredients.filter((i) => !linkedIds.has(i.id)),
    [allIngredients, linkedIds]
  );

  const filteredAvailable = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableIngredients;
    return availableIngredients.filter((i) =>
      i.name.toLowerCase().includes(q)
    );
  }, [availableIngredients, searchQuery]);

  const selectedIngredient = allIngredients.find(
    (i) => i.id === selectedIngredientId
  );

  const handleLink = async () => {
    if (!selectedIngredientId) {
      toast.error("Sélectionnez un ingrédient");
      return;
    }
    const normalizedQuantity = normalizeDecimalInput(quantity);
    if (normalizedQuantity === null || parseFloat(normalizedQuantity) <= 0) {
      toast.error("La quantité doit être supérieure à 0");
      return;
    }
    const result = await onLink({
      ingredient: `/api/restaurants/${restaurantId}/ingredients/${selectedIngredientId}`,
      quantity: normalizedQuantity,
    });
    if (result.ok) {
      toast.success("Ingrédient lié avec succès");
      setSelectedIngredientId("");
      setQuantity("");
      setSearchQuery("");
      close();
    } else {
      toast.error(result.message ?? "Erreur lors de la liaison");
    }
  };

  const handleRemove = async (mapping: RecipeIngredientLink) => {
    // The collection endpoint returns only { id, @id } for the embedded
    // ingredient, so resolve the name from the full ingredients list — the same
    // way the row above does — otherwise the confirmation title is empty.
    const full = allIngredients.find((i) => i.id === mapping.ingredient.id);
    const name = full?.name ?? mapping.ingredient.name ?? "";
    const ok = await confirm({
      title: `Retirer "${name}" ?`,
      description:
        "Cet ingrédient ne sera plus consommé par cette option.",
      confirmText: "Retirer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const result = await onRemove(mapping);
    if (!result.ok) {
      toast.error(result.message ?? "Erreur lors de la suppression");
      return;
    }
    toast.success("Ingrédient retiré avec succès");
  };

  const openEdit = (mapping: RecipeIngredientLink) => {
    setEditingMapping(mapping);
    setEditQuantity(trimQuantity(mapping.quantity));
  };

  const closeEdit = () => {
    setEditingMapping(null);
    setEditQuantity("");
  };

  const handleUpdate = async () => {
    if (!editingMapping || !onUpdate) return;
    const normalizedQuantity = normalizeDecimalInput(editQuantity);
    if (normalizedQuantity === null || parseFloat(normalizedQuantity) <= 0) {
      toast.error("La quantité doit être supérieure à 0");
      return;
    }
    const result = await onUpdate(editingMapping, {
      quantity: normalizedQuantity,
    });
    if (result.ok) {
      toast.success("Quantité mise à jour avec succès");
      closeEdit();
    } else {
      toast.error(result.message ?? "Erreur lors de la mise à jour");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="w-full h-10" />
        <Skeleton className="w-full h-10" />
        <Skeleton className="w-full h-10" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-card-foreground">
          Ingrédients liés
        </h4>
        {!readOnly && (
          <ButtonAdd label={addLabel} type="button" onClick={open} />
        )}
      </div>

      {linkedIngredients.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground text-sm">{emptyTitle}</p>
          <p className="text-muted-foreground text-xs mt-1">{emptySubtitle}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {linkedIngredients.map((mapping) => {
            // The collection endpoint returns only { id, @id } for the embedded
            // ingredient, so resolve name/unit from the full ingredients list.
            const full = allIngredients.find(
              (i) => i.id === mapping.ingredient.id
            );
            const name = full?.name ?? mapping.ingredient.name ?? "";
            const unit = full?.baseUnit ?? mapping.ingredient.baseUnit ?? "";
            return (
              <div
                key={mapping.id}
                className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-card-foreground">
                    {name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatStock(mapping.quantity, unit)} par unité
                  </p>
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-1">
                    {onUpdate && (
                      <ButtonEdit onClick={() => openEdit(mapping)} />
                    )}
                    <ButtonDelete onClick={() => handleRemove(mapping)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setSelectedIngredientId("");
            setQuantity("");
            setSearchQuery("");
            close();
          }}
          title={addLabel}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Ingrédient
              </label>
              <AsyncCombobox
                value={selectedIngredientId}
                onChange={setSelectedIngredientId}
                options={filteredAvailable.map((i) => ({
                  value: i.id,
                  label: i.name,
                  sublabel: i.baseUnit,
                }))}
                selectedLabel={
                  selectedIngredient
                    ? `${selectedIngredient.name} (${selectedIngredient.baseUnit})`
                    : undefined
                }
                placeholder="Sélectionner un ingrédient..."
                searchPlaceholder="Rechercher un ingrédient..."
                emptyMessage="Aucun ingrédient disponible"
                isLoading={isFetchingIngredients}
                onSearchChange={setSearchQuery}
              />
            </div>

            <div>
              <label
                htmlFor="recipe-quantity"
                className="block text-sm font-medium text-card-foreground mb-1"
              >
                Quantité par unité vendue
                {selectedIngredient && (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    ({selectedIngredient.baseUnit})
                  </span>
                )}
              </label>
              <input
                id="recipe-quantity"
                type="text"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: 0.5"
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                type="button"
                onClick={handleLink}
                isLoading={isLinking}
                size="sm"
              >
                Lier
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {!readOnly && editingMapping && (
        <Modal
          isOpen={!!editingMapping}
          onClose={closeEdit}
          title="Modifier la quantité"
        >
          {(() => {
            const full = allIngredients.find(
              (i) => i.id === editingMapping.ingredient.id
            );
            const name = full?.name ?? editingMapping.ingredient.name ?? "";
            const unit =
              full?.baseUnit ?? editingMapping.ingredient.baseUnit ?? "";
            return (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {name}
                </p>
                <div>
                  <label
                    htmlFor="recipe-edit-quantity"
                    className="block text-sm font-medium text-card-foreground mb-1"
                  >
                    Quantité par unité vendue
                    {unit && (
                      <span className="text-muted-foreground font-normal">
                        {" "}
                        ({unit})
                      </span>
                    )}
                  </label>
                  <input
                    id="recipe-edit-quantity"
                    type="text"
                    inputMode="decimal"
                    value={editQuantity}
                    onChange={(e) => setEditQuantity(e.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Ex: 0.5"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    onClick={handleUpdate}
                    isLoading={isUpdating}
                    size="sm"
                  >
                    Enregistrer
                  </Button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
};

export default RecipeIngredientsManager;
